import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg font-semibold whitespace-nowrap transition-colors disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        default: 'border border-transparent bg-primary text-white hover:bg-primary-dark',
        secondary: 'border border-input bg-surface text-fg-soft hover:bg-chip',
        'destructive-outline': 'border border-danger-border bg-surface text-danger hover:bg-danger-bg',
        ghost: 'border border-transparent bg-transparent text-muted hover:bg-chip',
      },
      size: {
        sm: 'px-3.5 py-2 text-[12.5px]',
        default: 'px-[18px] py-[11px] text-[13px]',
        lg: 'px-6 py-3 text-sm font-bold',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

export { buttonVariants };
